import { Dialog } from "primereact/dialog";
import { Toolbar } from "primereact/toolbar";
import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  createRole,
  deleteRole,
  getAllRole,
  updateRole,
} from "../../service/role/RoleService";
import { useDispatch, useSelector } from "react-redux";
import { TabView, TabPanel } from "primereact/tabview";
import { AllRoleItemDto } from "../../dtos/role/AllRoleItemDto";
import { InputText } from "primereact/inputtext";
import { getPermissionByRole } from "../../service/permission/permissionService";
import Permission from "../permission/Permission";
import { Toast } from "primereact/toast";
import { PermissionVariables } from "../../shared/constant/PermissionVariables";
import { Button } from "../ui/Button";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "primereact/card";
import { showToast } from '../../store/slices/toastSlice';

export interface IRole {
  id: number | null;
  tenantId: number | null;
  name: string;
  displayName: string;
  [key: string]: string | (number | null);
}

interface PermissionCheckState {
  [key: string]: {
    checked: boolean;
    partialChecked: boolean;
  };
}

export const Role = () => {
  const emptyRole: IRole = {
    id: null,
    tenantId: null,
    name: "",
    displayName: "",
  };
  const dispatch = useDispatch();
  const [showCreatePermissionDialog, setShowCreatePermissionDialog] =
    useState<boolean>(false);
  const [allRoles, setAllRoles] = useState<AllRoleItemDto[]>([]);
  const [selectPermission, setSelectPermission] = useState<any>(null);
  const [edit, setEdit] = useState<boolean>(false);
  const [role, setRole] = useState(emptyRole);
  const [obj, setObj] = useState({});
  const [activeTab, setActiveTab] = useState<number>(0);
  const toast = useRef<Toast>(null);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<any[] | []>([]);
  const [isCreateClicked, setIsCreateClicked] = useState<boolean>(false);
  const [saveType, setSaveType] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [originalRole, setOriginalRole] = useState<IRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<PermissionCheckState | {}>(
    {}
  );

  const userPermissions = useSelector(
    (state: any) => state.permission.permissions
  );

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const roles = await getAllRole(dispatch);
      if (roles) {
        setAllRoles(roles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      dispatch(showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load roles',
        life: 3000
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (e: any) => {
    setActiveTab(e.index);
  };

  const onInputChange = (e: any, name: string) => {
    const value: string = e.target.value || "";
    let roleData: IRole = { ...role };
    roleData[name] = value;

    setRole(roleData);
  };



  const onEdit = async (rowData: any) => {
    console.log("Row Data:", rowData);
    setSaveType("Edit");
    setShowCreatePermissionDialog(true);
    setEdit(true);

    const roleData: IRole = {
      id: rowData.id,
      tenantId: rowData.tenantId,
      name: rowData.name,
      displayName: rowData.displayName,
    };

    setRole(roleData);
    setOriginalRole(roleData);

    const permission = await getPermissionByRole(dispatch, {
      roleId: roleData.id,
      tenantId: roleData.tenantId
    });
    setPermissions(permission.permissions);
  };

  const openNew = () => {
    setSaveType("Create");
    setIsCreateClicked(true);
    setShowCreatePermissionDialog(true);
    setRole(emptyRole);
  };

  const handleDeleteRole = async (roleId: number) => {
    try {
      const response = await deleteRole(dispatch, roleId);
      if (response) {
        dispatch(showToast({
          severity: 'success',
          summary: 'Success',
          detail: 'Role deleted successfully',
        }));
        fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      dispatch(showToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete role',
        life: 3000
      }));
    }
  };


  const handleSave = async () => {
    if (!role.name.trim()) {
      setNameError("Role name is required.");
      dispatch(showToast({
        severity: "error",
        summary: "Error",
        detail: "Role name is required.",
        life: 3000
      }));
      return;
    }

    let roleId: number | null = role.id;

    if (edit) {
      if (
        originalRole &&
        (originalRole.name !== role.name ||
          originalRole.displayName !== role.displayName)
      ) {
        const updateRoleRes = await updateRole(
          dispatch,
          role,
          role.id as number
        );
        await fetchRoles();
        dispatch(showToast({
          severity: 'success',
          summary: 'Success',
          detail: typeof updateRoleRes === "string" ? updateRoleRes : "Role updated successfully",
          life: 3000
        }));
      }
    } else {
      const createdRoleRes = await createRole(dispatch, role);
      setRole(createdRoleRes.role);
      roleId = createdRoleRes.role.id
      await fetchRoles();
      dispatch(showToast({
        severity: 'success',
        summary: 'Success',
        detail: typeof createdRoleRes === "string" ? createdRoleRes : "Role created successfully",
        life: 3000
      }));
    }

    if (roleId) {
      setIsSaveClicked(true);
    } else {
      resetDialog();
    }
  };
  const resetDialog = async () => {
    setPermissions([])
    setNameError(null)
    setShowCreatePermissionDialog(false);
    setEdit(false);
    setRole(emptyRole)
    await setIsSaveClicked(false);
    setIsCreateClicked(false);
    setPermissions([]);
    setActiveTab(0);
    setNameError(null);
    setSelectedKeys({})
  };
  const hidePermissionDialog = () => {
    setIsSaveClicked(false);
    setEdit(false);
    setShowCreatePermissionDialog(false);
    setSelectPermission(null);
    setObj({});
    setIsCreateClicked(false);
    setPermissions([]);
    setActiveTab(0);
    setNameError(null);
    setSelectedKeys({})
  };

  const startToolbar = () => {
    return (
      <div>
        <h4 className="text-xl font-bold surface-900 mb-4 mt-3">Role</h4>
      </div>
    );
  };

  const endToolbar = () => {
    return (
      <div>
        {userPermissions?.includes(PermissionVariables.createRole) && <Button
          onClick={openNew}
          className="bg-gold text-white hover:bg-gold font-medium px-4 rounded-md h-10"
        >
          Create
        </Button>}
      </div>
    );
  };

  const actionBodyTemplate = (rowData: AllRoleItemDto) => {
    return (
      <div className="flex">
        {userPermissions?.includes(PermissionVariables.editRole) && <Button
          className="actionBtn mr-2 bg-transparent text-gold hover:text-white border-gold hover:bg-gold hover:border-gold"
          onClick={() => onEdit(rowData)}
          children={<FontAwesomeIcon icon={faPen} />}
        />}
        {userPermissions?.includes(PermissionVariables.deleteRole) && <Button
          className="actionBtn bg-transparent text-red-500 border-red-500 hover:bg-red-500  hover:text-white"
          onClick={() => handleDeleteRole(rowData.id as number)}
          children={<FontAwesomeIcon icon={faTrash} />}
        />}
      </div>
    );
  };

  const createRoleDialogFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          onClick={handleSave}
          className="bg-gold text-white hover:bg-gold font-medium px-4 rounded-md h-10 transition-colors"
        >
          Save
        </Button>
        <Button
          onClick={hidePermissionDialog}
          className="bg-transparent text-gold hover:text-white hover:bg-gold font-medium px-4 rounded-md h-10 transition-colors"
        >
          Cancel
        </Button>
      </div>
    );
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  return (
    <div className="p-0 role">
      <Toast ref={toast} />
      <Toolbar
        start={startToolbar}
        end={endToolbar}
        className="mb-2 bg-transparent border-none p-0"
      />

      <Card className="rounded-md shadow-sm card_content_padding ">
        <DataTable
          value={allRoles}
          className="p-datatable-sm"
          rowHover
          stripedRows
        >
          <Column field="name" header="Name" className="py-3 px-4"></Column>
          <Column
            field="displayName"
            header="Display Name"
            className="py-3 px-4"
          ></Column>
          {(userPermissions?.includes(PermissionVariables.createRole) && userPermissions?.includes(PermissionVariables.deleteRole)) && <Column
            header="Actions"
            body={actionBodyTemplate}
            className="py-3 px-4"
          ></Column>}
        </DataTable>
      </Card>

      <Dialog
        visible={showCreatePermissionDialog}
        header={saveType === "Create" ? "Create New Role" : "Edit Role"}
        onHide={hidePermissionDialog}
        style={{ width: "40rem" }}
        footer={createRoleDialogFooter}
        breakpoints={{ "960px": "75vw", "640px": "90vw" }}
        className="p-fluid tabPadding_0"
        contentClassName="border-t border-gray-light"
        headerClassName="text-xl font-semibold text-gray-dark"
      >
        <TabView
          activeIndex={activeTab}
          onTabChange={(e) => handleTabChange(e)}
          renderActiveOnly={false}
          className="mt-4"
        >
          <TabPanel header="Details">
            <div className="grid grid-cols-1 gap-4 p-0">
              <div className="">
                <label
                  className="block text-base font-medium mb-2 text-gray-dark"
                  htmlFor="name"
                >
                  Name
                </label>
                <InputText
                  id="name"
                  type="text"
                  value={role.name as string}
                  onChange={(e) => onInputChange(e, "name")}
                  className={
                    nameError
                      ? "p-invalid w-full h-10 rounded-md border-2 border-red px-4 shadow-sm"
                      : "w-full h-10 rounded-md border-2 border-gray-medium px-4 shadow-sm"
                  }
                  placeholder="Enter role name"
                />
                {nameError && (
                  <small className="text-red mt-1 block">{nameError}</small>
                )}
              </div>

              <div className="mb-4">
                <label
                  className="block text-base font-medium mb-2 text-gray-dark"
                  htmlFor="displayName"
                >
                  Display Name
                </label>
                <InputText
                  id="displayName"
                  value={role?.displayName as string}
                  onChange={(e) => onInputChange(e, "displayName")}
                  className="w-full h-10 rounded-md border-2 border-gray-medium px-4 shadow-sm focus:border-primary focus:bg-primary-light focus:bg-opacity-10"
                  placeholder="Enter display name"
                />
              </div>
            </div>
          </TabPanel>

          <TabPanel header="Permissions">
            <Permission
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              componentInfo={"Role"}
              allPermissions={permissions}
              resetDialog={resetDialog}
              roleInfo={role}
              isSaveClicked={isSaveClicked}
            />
          </TabPanel>
        </TabView>
      </Dialog>
    </div>
  );
};
